// ISO 3166-1 alpha-2 codes only; names come from `Intl.DisplayNames` so no list ships in the bundle.

export const COMPACT_COUNTRIES = ['CH', 'DE', 'AT', 'LI', 'FR', 'IT', 'GB', 'US'] as const;

export const ALL_COUNTRIES: readonly string[] = (
  'AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS ' +
  'BT BV BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE ' +
  'EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM ' +
  'HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC ' +
  'LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA ' +
  'NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW ' +
  'SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO ' +
  'TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW'
).split(' ');

const displayNames = new Map<string, Intl.DisplayNames | null>();

export function countryName(code: string, locale: string): string {
  let names = displayNames.get(locale);
  if (names === undefined) {
    try {
      names = new Intl.DisplayNames([locale], { type: 'region' });
    } catch {
      names = null;
    }
    displayNames.set(locale, names);
  }
  try {
    return names?.of(code) ?? code;
  } catch {
    return code;
  }
}

export interface CountryOption {
  code: string;
  name: string;
}

export function countryOptions(
  codes: readonly string[],
  locale: string,
  sort = true
): CountryOption[] {
  const options = codes.map((code) => ({ code, name: countryName(code, locale) }));
  if (!sort) return options;
  let collator: Intl.Collator | undefined;
  try {
    collator = new Intl.Collator(locale);
  } catch {
    collator = undefined;
  }
  return options.sort((a, b) =>
    collator ? collator.compare(a.name, b.name) : a.name < b.name ? -1 : 1
  );
}

// Zones that pin a phone country well enough to preselect it; everything else stays unset.
const ZONE_TO_COUNTRY: Record<string, string> = {
  'Europe/Zurich': 'CH',
  'Europe/Berlin': 'DE',
  'Europe/Vienna': 'AT',
  'Europe/Vaduz': 'LI',
  'Europe/Paris': 'FR',
  'Europe/Rome': 'IT',
  'Europe/London': 'GB',
};

/** Best-effort default for the phone country: locale region first, then a few well-known zones. */
export function guessCountry(locale: string, timeZone: string): string | undefined {
  try {
    const region = new Intl.Locale(locale).region;
    if (region && ALL_COUNTRIES.includes(region)) return region;
  } catch {
    // ignore malformed locales
  }
  return ZONE_TO_COUNTRY[timeZone];
}
